/**
 * curveConverter — CIRCLE / ARC / ELLIPSE → CadPrimitive paths.
 *
 * All three become TRUE cubic-bezier paths (no facet error): a full CIRCLE is
 * a closed 4-quarter loop; an ARC is an open bezier span chain; an ELLIPSE /
 * ellipse-arc uses the axis-rotated parametric bezier conversion.
 *
 * Angle units: DXF ARC `startAngle`/`endAngle` are **degrees** CCW
 * (`arcToRenderable.ts` confirms), so we convert to radians here. ELLIPSE
 * `startParam`/`endParam` are already radians (model schema).
 *
 * RAW world mm coordinates (no applyOffset).
 */

import { computeExplodedEllipseGeometry } from "@dxf-ai-editor/command-engine";
import type {
  ArcEntity,
  CircleEntity,
  EllipseEntity,
} from "@dxf-ai-editor/drawing-model";

import type { CadConvertContext, CadPrimitive } from "../cadPrimitive";
import {
  arcSpansToBeziers,
  circleToBeziers,
  circularArcToBeziers,
  ellipseToBeziers,
} from "./arcBezier";
import { resolvePlotStyle } from "./resolveStyle";
import { isDefaultOcs } from "./ocs";

const DEG_TO_RAD = Math.PI / 180;

export function circleToPrimitives(
  entity: CircleEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const style = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (style.skip) return [];
  const { start, segments } = circleToBeziers(
    entity.center.x,
    entity.center.y,
    entity.radius,
  );
  return [
    {
      type: "path",
      entityId: entity.id,
      layer: entity.layer ?? "0",
      start,
      segments,
      closed: true,
      fill: "none",
      style,
    },
  ];
}

export function arcToPrimitives(
  entity: ArcEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const style = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (style.skip) return [];
  const { start, segments } = circularArcToBeziers(
    entity.center.x,
    entity.center.y,
    entity.radius,
    entity.startAngle * DEG_TO_RAD,
    entity.endAngle * DEG_TO_RAD,
    true, // DXF ARC always CCW
  );
  return [
    {
      type: "path",
      entityId: entity.id,
      layer: entity.layer ?? "0",
      start,
      segments,
      closed: false,
      fill: "none",
      style,
    },
  ];
}

export function ellipseToPrimitives(
  entity: EllipseEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const style = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (style.skip) return [];
  // A DXF ELLIPSE is fully 3D in WCS: `center` (10/20/30) and `majorAxisEndpoint`
  // (11/21/31 — a VECTOR from centre) are WCS points, and `extrusion`
  // (210/220/230) is the plane normal fixing the in-plane minor direction
  // (`minor = extrusion × major`). The exporter is an orthographic top-down (world
  // XY) plot, so we PROJECT the true 3D ellipse onto the page by dropping world z.
  //
  // SL-15 round-9 @codex F3 — pre-fix this read only `majorAxisEndpoint.x/y` and
  // OCS-lifted them, which (a) dropped the real major-axis z (DXF 31, now imported
  // by SL-15) so a tilted ellipse FLATTENED, and (b) wrongly treated the WCS
  // centre/major as OCS coords. The orthographic projection of an ellipse (a
  // planar conic) is itself an ellipse, but the projected major/minor are CONJUGATE
  // (generally non-orthogonal) semi-diameters — `ellipseToBeziers` needs the
  // PRINCIPAL major + ratio, so recover them with the ONE-TRUTH
  // `computeExplodedEllipseGeometry` driven by the projection transform
  // (identity XY affine, `scaleZ = 0` ⇒ drop world z). It maps the 3D major axis
  // through the linear part, derives the 3D minor from the extrusion, projects
  // both, runs Rytz when non-orthogonal, and re-anchors an open arc's params.
  const extrusion = entity.extrusion ?? { x: 0, y: 0, z: 1 };
  const planarDefault =
    isDefaultOcs(extrusion) &&
    (entity.center.z === undefined || entity.center.z === 0) &&
    (entity.majorAxisEndpoint.z === undefined ||
      entity.majorAxisEndpoint.z === 0);

  let cx: number;
  let cy: number;
  let majX: number;
  let majY: number;
  let ratio: number;
  let startParam: number;
  let endParam: number;
  // SL-15 round-10 @codex F4 — sweep direction. A DXF ELLIPSE's params are CCW in
  // its OCS plane; a MIRRORED normal flips that handedness (CCW-in-OCS = CW-in-paper).
  // Default true (non-mirrored) keeps every existing arc byte-identical.
  let counterclockwise = true;
  if (planarDefault) {
    // Planar ellipse in the WCS XY plane with the default normal — the projection
    // is the identity, so use the raw fields directly (BYTE-IDENTICAL to the
    // pre-fix planar path: same cx/cy/major/ratio/params, no float noise).
    cx = entity.center.x;
    cy = entity.center.y;
    majX = entity.majorAxisEndpoint.x;
    majY = entity.majorAxisEndpoint.y;
    ratio = entity.ratio;
    startParam = entity.startParam;
    endParam = entity.endParam;
  } else {
    // Tilted / elevated / non-default-normal ellipse — project the full 3D shape
    // onto the page (world z dropped) and recover its principal axes.
    const projected = computeExplodedEllipseGeometry(
      {
        center: entity.center,
        majorAxisEndpoint: entity.majorAxisEndpoint,
        ratio: entity.ratio,
        extrusion,
        startParam: entity.startParam,
        endParam: entity.endParam,
      },
      {
        matrix: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
        scaleZ: 0, // orthographic top-down: collapse world z onto the page plane
        insertionZ: 0,
        basePointZ: 0,
      },
    );
    cx = projected.center.x;
    cy = projected.center.y;
    majX = projected.majorAxisEndpoint.x;
    majY = projected.majorAxisEndpoint.y;
    ratio = projected.ratio;
    const s = projected.startParam ?? entity.startParam;
    const e = projected.endParam ?? entity.endParam;
    // SL-15 round-10 @codex F4 — the projected OCS normal (always ±z after the
    // drop-z projection) carries the handedness. A mirrored normal (z < 0) means the
    // param sweep is CW in paper. `ellipseToBeziers`'s minor is fixed +90° (CCW), so
    // the mirrored arc is reproduced by NEGATING the params AND sweeping CW — verified
    // against the `tessellateEllipse` render oracle (start / end / arc-span all match;
    // discarding the normal + drawing CCW mirrored the arc across the major axis). A
    // non-mirrored (0,0,1) normal is unchanged (params as-is, CCW).
    if (projected.extrusion.z < 0) {
      startParam = -s;
      endParam = -e;
      counterclockwise = false;
    } else {
      startParam = s;
      endParam = e;
    }
  }

  const isFull =
    Math.abs(endParam - startParam) >= Math.PI * 2 - 1e-9 ||
    (startParam === 0 && endParam >= Math.PI * 2 - 1e-9);
  const { start, segments } = ellipseToBeziers(
    cx,
    cy,
    majX,
    majY,
    ratio,
    startParam,
    endParam,
    counterclockwise,
  );
  return [
    {
      type: "path",
      entityId: entity.id,
      layer: entity.layer ?? "0",
      start,
      segments,
      closed: isFull,
      fill: "none",
      style,
    },
  ];
}

// Re-export the low-level span helper for the INSERT converter (which builds
// transformed arc/ellipse beziers via custom eval functions).
export { arcSpansToBeziers };
